<template>
  <div class="alice-an">
    <header class="alice-an__head">
      <div class="alice-an__head-main">
        <div class="alice-an__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Analytics</span>
        </div>
        <h1 class="alice-an__title">
          Alice Analytics
          <span v-if="data" class="alice-an__count">{{
            data.kpis.totalMessages.toLocaleString("en-GB")
          }}</span>
        </h1>
        <p class="alice-an__subtitle">
          How researchers are using Alice: chat activity, AI cost and skills.
        </p>
      </div>

      <div class="alice-an__seg" role="group" aria-label="Time window">
        <button
          v-for="w in windows"
          :key="w.key"
          type="button"
          class="alice-an__seg-btn"
          :class="{ 'alice-an__seg-btn--active': windowDays === w.value }"
          :aria-pressed="windowDays === w.value"
          @click="setWindow(w.value)"
        >
          {{ w.label }}
        </button>
      </div>
    </header>

    <!-- The header scrolls away; the tabs stay in view. -->
    <div class="alice-an__tabs" role="tablist" aria-label="Analytics">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="alice-an__tab"
        :class="{ 'alice-an__tab--active': tab === t.key }"
        role="tab"
        :aria-selected="tab === t.key"
        @click="setTab(t.key)"
      >
        {{ t.label }}
      </button>
    </div>

    <div v-if="loading" class="alice-an__state">Loading analytics…</div>
    <div v-else-if="error" class="alice-an__state alice-an__state--error">
      {{ error }}
    </div>
    <div v-else-if="data" class="alice-an__body">
      <!-- KPIs (each tab has its own four) -->
      <div v-if="headlineTiles.length" class="alice-an__kpis">
        <div
          v-for="(tile, i) in headlineTiles"
          :key="tile.label"
          class="alice-an__kpi"
        >
          <div class="alice-an__kpi-row">
            <span
              class="alice-an__kpi-swatch"
              :style="{ background: tile.color }"
            />
            <span class="alice-an__kpi-value">{{ tile.value }}</span>
          </div>
          <div class="alice-an__kpi-label">{{ tile.label }}</div>
          <div class="alice-an__kpi-sub">{{ tile.sub }}</div>
          <span
            v-if="i < headlineTiles.length - 1"
            class="alice-an__kpi-divider"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- ============== Activity tab ============== -->
      <template v-if="tab === 'activity'">
        <section class="alice-an__section">
          <div class="alice-an__section-head">
            <div class="alice-an__section-title">Daily activity</div>
            <div class="alice-an__section-sub">
              Messages sent per day · {{ trendDays.length }} days
            </div>
          </div>
          <div v-if="trendDays.length" class="alice-an__trend">
            <div
              v-for="d in trendDays"
              :key="d.date"
              class="alice-an__trend-bar"
              :title="`${formatDate(d.date)} · ${d.count} messages`"
            >
              <span
                class="alice-an__trend-fill"
                :style="{ height: `${trendBarHeight(d.count)}%` }"
              />
            </div>
          </div>
          <div v-else class="alice-an__empty">No activity in this window.</div>
        </section>

        <div class="alice-an__panel-head">
          <div class="alice-an__section-title">
            {{ activityView === "researchers" ? "Researchers" : "Projects" }}
          </div>
          <div class="alice-an__seg" role="group" aria-label="Breakdown">
            <button
              type="button"
              class="alice-an__seg-btn"
              :class="{
                'alice-an__seg-btn--active': activityView === 'researchers',
              }"
              :aria-pressed="activityView === 'researchers'"
              @click="activityView = 'researchers'"
            >
              Researchers
              <span class="alice-an__seg-count">{{
                data.researchers.length
              }}</span>
            </button>
            <button
              type="button"
              class="alice-an__seg-btn"
              :class="{
                'alice-an__seg-btn--active': activityView === 'projects',
              }"
              :aria-pressed="activityView === 'projects'"
              @click="activityView = 'projects'"
            >
              Projects
              <span class="alice-an__seg-count">{{
                data.projects.length
              }}</span>
            </button>
          </div>
        </div>

        <!-- Researchers -->
        <div v-if="activityView === 'researchers'" class="alice-an__panel">
          <div v-if="!data.researchers.length" class="alice-an__empty">
            No researchers active in this window.
          </div>
          <table v-else class="alice-an__table">
            <thead>
              <tr>
                <th class="alice-an__th alice-an__th--name">Researcher</th>
                <th class="alice-an__th alice-an__th--num">Messages</th>
                <th class="alice-an__th alice-an__th--num">Active days</th>
                <th class="alice-an__th alice-an__th--num">Attachments</th>
                <th class="alice-an__th">Last seen</th>
                <th class="alice-an__th">Top project</th>
                <th class="alice-an__th alice-an__th--chev"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="r in data.researchers" :key="r.uid">
                <tr
                  class="alice-an__tr alice-an__tr--clickable"
                  :class="{ 'alice-an__tr--open': expandedUser === r.uid }"
                  @click="toggleUser(r.uid)"
                >
                  <td class="alice-an__td alice-an__td--name">
                    <div class="alice-an__person">
                      <div
                        class="alice-an__avatar"
                        :style="
                          r.photo ? { backgroundImage: `url(${r.photo})` } : {}
                        "
                      >
                        <span v-if="!r.photo">{{ initials(r) }}</span>
                      </div>
                      <div class="alice-an__person-text">
                        <div class="alice-an__person-name">
                          {{ r.name || r.email || r.uid }}
                        </div>
                        <div v-if="r.email" class="alice-an__person-sub">
                          {{ r.email }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.messages.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.activeDays }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.attachments }}
                  </td>
                  <td class="alice-an__td alice-an__td--mono">
                    {{ formatRelative(r.lastSeen) }}
                  </td>
                  <td class="alice-an__td alice-an__td--ellipsis">
                    <span v-if="r.topProject">{{ r.topProject.title }}</span>
                    <span v-else class="alice-an__muted">—</span>
                  </td>
                  <td class="alice-an__td alice-an__td--chev">
                    <Icon
                      :name="
                        expandedUser === r.uid
                          ? 'chevron-down'
                          : 'chevron-right'
                      "
                      :size="14"
                    />
                  </td>
                </tr>
                <tr v-if="expandedUser === r.uid" class="alice-an__expand">
                  <td :colspan="7" class="alice-an__expand-cell">
                    <div class="alice-an__expand-grid">
                      <div class="alice-an__expand-block">
                        <div class="alice-an__expand-label">
                          Per project ({{ r.projects.length }})
                        </div>
                        <ul class="alice-an__bars">
                          <li
                            v-for="p in r.projects"
                            :key="p.uid"
                            class="alice-an__bar-row"
                          >
                            <span class="alice-an__bar-label">{{
                              p.title
                            }}</span>
                            <span class="alice-an__bar-track">
                              <span
                                class="alice-an__bar-fill"
                                :style="{
                                  width: `${
                                    (p.messages / r.projects[0].messages) * 100
                                  }%`,
                                }"
                              />
                            </span>
                            <span class="alice-an__bar-value">{{
                              p.messages
                            }}</span>
                          </li>
                        </ul>
                      </div>
                      <div class="alice-an__expand-block">
                        <div class="alice-an__expand-label">
                          Daily activity in window
                        </div>
                        <div
                          v-if="r.dailyTrend.length"
                          class="alice-an__trend alice-an__trend--mini"
                        >
                          <div
                            v-for="d in r.dailyTrend"
                            :key="d.date"
                            class="alice-an__trend-bar"
                            :title="`${formatDate(d.date)} · ${d.count}`"
                          >
                            <span
                              class="alice-an__trend-fill"
                              :style="{
                                height: `${userBarHeight(d.count, r)}%`,
                              }"
                            />
                          </div>
                        </div>
                        <div v-else class="alice-an__muted">No activity.</div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Projects -->
        <div v-if="activityView === 'projects'" class="alice-an__panel">
          <div v-if="!data.projects.length" class="alice-an__empty">
            No projects with chat activity in this window.
          </div>
          <table v-else class="alice-an__table">
            <thead>
              <tr>
                <th class="alice-an__th alice-an__th--name">Project</th>
                <th class="alice-an__th alice-an__th--num">Messages</th>
                <th class="alice-an__th alice-an__th--num">Researchers</th>
                <th class="alice-an__th">Last activity</th>
                <th class="alice-an__th">Status</th>
                <th class="alice-an__th alice-an__th--chev"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="p in data.projects" :key="p.uid">
                <tr
                  class="alice-an__tr alice-an__tr--clickable"
                  :class="{ 'alice-an__tr--open': expandedProject === p.uid }"
                  @click="toggleProject(p.uid)"
                >
                  <td class="alice-an__td alice-an__td--name">
                    <div class="alice-an__person-text">
                      <div class="alice-an__person-name">{{ p.title }}</div>
                      <div v-if="p.client" class="alice-an__person-sub">
                        {{ p.client }}
                      </div>
                    </div>
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ p.messages.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ p.researchers }}
                  </td>
                  <td class="alice-an__td alice-an__td--mono">
                    {{ formatRelative(p.lastSeen) }}
                  </td>
                  <td class="alice-an__td">
                    <span v-if="p.status" class="alice-an__pill">{{
                      p.status
                    }}</span>
                    <span v-else class="alice-an__muted">—</span>
                  </td>
                  <td class="alice-an__td alice-an__td--chev">
                    <Icon
                      :name="
                        expandedProject === p.uid
                          ? 'chevron-down'
                          : 'chevron-right'
                      "
                      :size="14"
                    />
                  </td>
                </tr>
                <tr v-if="expandedProject === p.uid" class="alice-an__expand">
                  <td :colspan="6" class="alice-an__expand-cell">
                    <div class="alice-an__expand-block">
                      <div class="alice-an__expand-label">
                        Contributors ({{ p.contributors.length }})
                      </div>
                      <ul class="alice-an__bars">
                        <li
                          v-for="c in p.contributors"
                          :key="c.uid"
                          class="alice-an__bar-row"
                        >
                          <span class="alice-an__bar-label">
                            {{ c.name || c.email || c.uid }}
                          </span>
                          <span class="alice-an__bar-track">
                            <span
                              class="alice-an__bar-fill"
                              :style="{
                                width: `${
                                  (c.messages / p.contributors[0].messages) *
                                  100
                                }%`,
                              }"
                            />
                          </span>
                          <span class="alice-an__bar-value">{{
                            c.messages
                          }}</span>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ============== Tokens & cost tab ============== -->
      <template v-if="tab === 'cost'">
        <!-- Token usage -->
        <section v-if="data.usage" class="alice-an__section">
          <div class="alice-an__section-head">
            <div class="alice-an__section-title">Token usage</div>
            <div class="alice-an__section-sub">
              LLM tokens, estimated cost and footprint<template
                v-if="data.usage.since"
              >
                · tracking since {{ formatDate(data.usage.since) }}</template
              >
              ·
              <a
                :href="energySourceUrl"
                target="_blank"
                rel="noopener"
                class="alice-an__usage-source"
                >About the energy estimate</a
              >
            </div>
          </div>

          <div v-if="!data.usage.totals.requests" class="alice-an__empty">
            No token usage recorded in this window.<template
              v-if="data.usage.since"
            >
              Tracking started {{ formatDate(data.usage.since) }}.</template
            >
          </div>
          <template v-else>
            <div v-if="usageBreakdown.length" class="alice-an__usage-breakdown">
              <span v-for="b in usageBreakdown" :key="b.label">
                <strong>{{ b.value }}</strong> {{ b.label }}
              </span>
            </div>

            <div
              v-if="usageTrendDays.length"
              class="alice-an__trend alice-an__trend--usage"
            >
              <div
                v-for="d in usageTrendDays"
                :key="d.date"
                class="alice-an__trend-bar"
                :title="`${formatDate(d.date)} · ${totalTokens(
                  d
                ).toLocaleString('en-GB')} tokens · ${formatEur(d.costEur)}`"
              >
                <span
                  class="alice-an__trend-fill"
                  :style="{ height: `${usageBarHeight(d.costEur)}%` }"
                />
              </div>
            </div>

            <div class="alice-an__expand-grid alice-an__usage-grid">
              <div class="alice-an__expand-block">
                <div class="alice-an__expand-label">
                  By model ({{ data.usage.byModel.length }})
                </div>
                <ul class="alice-an__bars">
                  <li
                    v-for="m in data.usage.byModel"
                    :key="m.model"
                    class="alice-an__bar-row"
                  >
                    <span class="alice-an__bar-label alice-an__usage-model">
                      <span
                        class="alice-an__eco-badge"
                        :class="`alice-an__eco-badge--${m.ecoTier}`"
                        :title="`${m.ecoTier} energy use`"
                      >
                        <Icon name="leaf" :size="12" />
                      </span>
                      {{ modelTitle(m.model) }}
                    </span>
                    <span class="alice-an__bar-track">
                      <span
                        class="alice-an__bar-fill"
                        :style="{ width: `${usageBarWidth(m)}%` }"
                      />
                    </span>
                    <span class="alice-an__bar-value alice-an__bar-value--wide">
                      {{ formatEur(m.costEur) }} ·
                      {{ formatTokens(totalTokens(m)) }}
                    </span>
                  </li>
                </ul>
              </div>
              <div class="alice-an__expand-block">
                <div class="alice-an__expand-label">
                  By researcher ({{ data.usage.byUser.length }})
                </div>
                <ul class="alice-an__bars">
                  <li
                    v-for="u in data.usage.byUser"
                    :key="u.uid"
                    class="alice-an__bar-row"
                  >
                    <span class="alice-an__bar-label">
                      {{ u.name || u.email || u.uid }}
                    </span>
                    <span class="alice-an__bar-track">
                      <span
                        class="alice-an__bar-fill"
                        :style="{ width: `${usageUserBarWidth(u)}%` }"
                      />
                    </span>
                    <span class="alice-an__bar-value alice-an__bar-value--wide">
                      {{ formatEur(u.costEur) }} ·
                      {{ formatTokens(totalTokens(u)) }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </section>

        <!-- Cost per month per project -->
        <section v-if="data.usage" class="alice-an__section">
          <div class="alice-an__section-head">
            <div class="alice-an__section-title">
              Cost per month per project
            </div>
            <div class="alice-an__section-sub">
              All tracked history — not affected by the time window above
            </div>
          </div>

          <div v-if="!monthCost.length" class="alice-an__empty">
            No cost recorded yet.<template v-if="data.usage.since">
              Tracking started {{ formatDate(data.usage.since) }}.</template
            >
          </div>
          <template v-else>
            <div class="alice-an__stack">
              <div
                v-for="m in monthCostChart"
                :key="m.month"
                class="alice-an__stack-col"
              >
                <div class="alice-an__stack-bar">
                  <span
                    class="alice-an__stack-track"
                    :style="{ height: `${monthBarHeight(m.costEur)}%` }"
                  >
                    <span
                      v-for="seg in m.segments"
                      :key="seg.key"
                      class="alice-an__stack-seg"
                      :class="{
                        'alice-an__stack-seg--rest': seg.unattributed,
                      }"
                      :style="{
                        height: `${seg.share}%`,
                        background: seg.color,
                      }"
                      :title="`${monthLabel(m.month)} · ${
                        seg.title
                      } · ${formatEur(seg.costEur)}`"
                    />
                  </span>
                </div>
                <div class="alice-an__stack-total">
                  {{ formatEur(m.costEur) }}
                </div>
                <div class="alice-an__stack-label">
                  {{ monthShortLabel(m.month) }}
                </div>
              </div>
            </div>

            <ul class="alice-an__legend">
              <li
                v-for="l in monthLegend"
                :key="l.key"
                class="alice-an__legend-item"
              >
                <span
                  class="alice-an__legend-swatch"
                  :style="{ background: l.color }"
                />
                {{ l.title }}
              </li>
            </ul>

            <table class="alice-an__table alice-an__table--months">
              <thead>
                <tr>
                  <th class="alice-an__th alice-an__th--name">Month</th>
                  <th class="alice-an__th alice-an__th--num">Cost</th>
                  <th class="alice-an__th alice-an__th--num">Requests</th>
                  <th class="alice-an__th alice-an__th--num">Tokens</th>
                  <th class="alice-an__th alice-an__th--num">Projects</th>
                  <th class="alice-an__th">Top project</th>
                  <th class="alice-an__th alice-an__th--chev" />
                </tr>
              </thead>
              <tbody>
                <template v-for="m in monthCost" :key="m.month">
                  <tr
                    class="alice-an__tr alice-an__tr--clickable"
                    :class="{ 'alice-an__tr--open': expandedMonth === m.month }"
                    @click="toggleMonth(m.month)"
                  >
                    <td class="alice-an__td alice-an__td--name">
                      {{ monthLabel(m.month) }}
                    </td>
                    <td class="alice-an__td alice-an__td--num">
                      {{ formatEur(m.costEur) }}
                    </td>
                    <td class="alice-an__td alice-an__td--num">
                      {{ m.requests.toLocaleString("en-GB") }}
                    </td>
                    <td class="alice-an__td alice-an__td--num">
                      {{ formatTokens(totalTokens(m)) }}
                    </td>
                    <td class="alice-an__td alice-an__td--num">
                      {{ m.projectCount }}
                    </td>
                    <td class="alice-an__td">
                      <span v-if="m.topProject">{{ m.topProject.title }}</span>
                      <span v-else class="alice-an__muted">—</span>
                    </td>
                    <td class="alice-an__td alice-an__td--chev">
                      <Icon
                        :name="
                          expandedMonth === m.month
                            ? 'chevron-down'
                            : 'chevron-right'
                        "
                        :size="14"
                      />
                    </td>
                  </tr>
                  <tr v-if="expandedMonth === m.month" class="alice-an__expand">
                    <td :colspan="7" class="alice-an__expand-cell">
                      <div class="alice-an__expand-block">
                        <div class="alice-an__expand-label">
                          Per project ({{ m.projectCount }})
                        </div>
                        <ul class="alice-an__bars">
                          <li
                            v-for="p in m.projects"
                            :key="p.uid || 'none'"
                            class="alice-an__bar-row"
                          >
                            <span
                              class="alice-an__bar-label"
                              :class="{ 'alice-an__muted': p.unattributed }"
                            >
                              {{ p.title }}
                            </span>
                            <span class="alice-an__bar-track">
                              <span
                                class="alice-an__bar-fill"
                                :class="{
                                  'alice-an__bar-fill--rest': p.unattributed,
                                }"
                                :style="{
                                  width: `${monthProjectWidth(m, p)}%`,
                                }"
                              />
                            </span>
                            <span
                              class="alice-an__bar-value alice-an__bar-value--wide"
                            >
                              {{ formatEur(p.costEur) }} ·
                              {{ monthShare(m, p) }}
                            </span>
                          </li>
                        </ul>
                        <div
                          v-if="m.sources.length"
                          class="alice-an__stack-note"
                          :title="'Cost is tracked per model and per user, never per feature — these are request counts only.'"
                        >
                          Requests by feature: {{ sourceSummary(m.sources) }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </section>
      </template>

      <!-- ============== Skills tab ============== -->
      <template v-if="tab === 'skills'">
        <section class="alice-an__section">
          <div class="alice-an__section-head">
            <div class="alice-an__section-title">Answers with a skill</div>
            <div class="alice-an__section-sub">
              Alice's answers per day in chat and team chat; the dark part had a
              skill active · {{ skillTrendDays.length }} days
            </div>
          </div>
          <template v-if="skillTrendDays.length">
            <div class="alice-an__trend">
              <div
                v-for="d in skillTrendDays"
                :key="d.date"
                class="alice-an__trend-bar"
                :title="`${formatDate(d.date)} · ${d.withSkill} of ${
                  d.answers
                } answers had a skill active`"
              >
                <span
                  class="alice-an__trend-fill alice-an__trend-fill--total"
                  :style="{ height: `${skillBarHeight(d.answers)}%` }"
                >
                  <span
                    class="alice-an__trend-part"
                    :style="{ height: `${percent(d.withSkill, d.answers)}%` }"
                  />
                </span>
              </div>
            </div>
            <ul class="alice-an__legend">
              <li class="alice-an__legend-item">
                <span
                  class="alice-an__legend-swatch"
                  style="background: var(--vp-black)"
                />
                With a skill
              </li>
              <li class="alice-an__legend-item">
                <span
                  class="alice-an__legend-swatch"
                  style="background: var(--vp-grijs-40)"
                />
                Without
              </li>
            </ul>
          </template>
          <div v-else class="alice-an__empty">No answers in this window.</div>
        </section>

        <div class="alice-an__panel-head">
          <div>
            <div class="alice-an__section-title">
              {{ skillsView === "skills" ? "Per skill" : "Per researcher" }}
            </div>
            <div class="alice-an__section-sub">{{ skillsSourceNote }}</div>
          </div>
          <div class="alice-an__seg" role="group" aria-label="Breakdown">
            <button
              type="button"
              class="alice-an__seg-btn"
              :class="{ 'alice-an__seg-btn--active': skillsView === 'skills' }"
              :aria-pressed="skillsView === 'skills'"
              @click="skillsView = 'skills'"
            >
              Skills
              <span class="alice-an__seg-count">{{
                data.skills.list.length
              }}</span>
            </button>
            <button
              type="button"
              class="alice-an__seg-btn"
              :class="{
                'alice-an__seg-btn--active': skillsView === 'researchers',
              }"
              :aria-pressed="skillsView === 'researchers'"
              @click="skillsView = 'researchers'"
            >
              Researchers
              <span class="alice-an__seg-count">{{
                data.skills.researchers.length
              }}</span>
            </button>
          </div>
        </div>

        <!-- Per skill -->
        <div v-if="skillsView === 'skills'" class="alice-an__panel">
          <div v-if="!data.skills.list.length" class="alice-an__empty">
            No skills in the library yet.
          </div>
          <table v-else class="alice-an__table">
            <thead>
              <tr>
                <th class="alice-an__th alice-an__th--name">Skill</th>
                <th class="alice-an__th alice-an__th--num">Uses</th>
                <th class="alice-an__th alice-an__th--num">Chat</th>
                <th class="alice-an__th alice-an__th--num">Team chat</th>
                <th class="alice-an__th alice-an__th--num">MCP</th>
                <th class="alice-an__th alice-an__th--num">Researchers</th>
                <th
                  class="alice-an__th alice-an__th--num"
                  title="Times Alice loaded the skill on its own because the request matched its description"
                >
                  Picked by Alice
                </th>
                <th class="alice-an__th">Last used</th>
                <th class="alice-an__th alice-an__th--chev"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="s in data.skills.list" :key="s.id">
                <tr
                  class="alice-an__tr alice-an__tr--clickable"
                  :class="{
                    'alice-an__tr--open': expandedSkill === s.id,
                    'alice-an__tr--idle': !s.uses,
                  }"
                  @click="toggleSkill(s.id)"
                >
                  <td class="alice-an__td alice-an__td--name">
                    {{ s.title || s.name || s.id }}
                    <span
                      v-if="s.status !== 'published'"
                      class="alice-an__muted"
                    >
                      · {{ skillStatusLabel(s.status) }}</span
                    >
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.uses.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.bySource.chat.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.bySource.communal.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.bySource.mcp.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.researcherCount }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ s.autoLoads.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--mono">
                    <span v-if="s.lastUsedAt">{{
                      formatRelative(s.lastUsedAt)
                    }}</span>
                    <span v-else class="alice-an__muted">never</span>
                  </td>
                  <td class="alice-an__td alice-an__td--chev">
                    <Icon
                      :name="
                        expandedSkill === s.id
                          ? 'chevron-down'
                          : 'chevron-right'
                      "
                      :size="14"
                    />
                  </td>
                </tr>
                <tr v-if="expandedSkill === s.id" class="alice-an__expand">
                  <td :colspan="9" class="alice-an__expand-cell">
                    <div v-if="!s.uses" class="alice-an__muted">
                      Not used in this window.
                    </div>
                    <div
                      v-else
                      class="alice-an__expand-grid alice-an__expand-grid--even"
                    >
                      <div class="alice-an__expand-block">
                        <div class="alice-an__expand-label">
                          Researchers ({{ s.researchers.length }})
                        </div>
                        <ul v-if="s.researchers.length" class="alice-an__bars">
                          <li
                            v-for="u in s.researchers"
                            :key="u.uid"
                            class="alice-an__bar-row"
                          >
                            <span class="alice-an__bar-label">
                              {{ u.name || u.email || u.uid }}
                            </span>
                            <span class="alice-an__bar-track">
                              <span
                                class="alice-an__bar-fill"
                                :style="{
                                  width: `${percent(
                                    u.uses,
                                    s.researchers[0].uses
                                  )}%`,
                                }"
                              />
                            </span>
                            <span class="alice-an__bar-value">{{
                              u.uses
                            }}</span>
                          </li>
                        </ul>
                        <div v-else class="alice-an__muted">
                          Only fetched over MCP, which doesn't say by whom.
                        </div>
                      </div>
                      <div class="alice-an__expand-block">
                        <div class="alice-an__expand-label">
                          Projects ({{ s.projects.length }})
                        </div>
                        <ul v-if="s.projects.length" class="alice-an__bars">
                          <li
                            v-for="p in s.projects"
                            :key="p.uid"
                            class="alice-an__bar-row"
                          >
                            <span class="alice-an__bar-label">
                              {{ p.title || "Unknown project" }}
                            </span>
                            <span class="alice-an__bar-track">
                              <span
                                class="alice-an__bar-fill"
                                :style="{
                                  width: `${percent(
                                    p.uses,
                                    s.projects[0].uses
                                  )}%`,
                                }"
                              />
                            </span>
                            <span class="alice-an__bar-value">{{
                              p.uses
                            }}</span>
                          </li>
                        </ul>
                        <div v-else class="alice-an__muted">
                          Only fetched over MCP, outside any project.
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Per researcher -->
        <div v-if="skillsView === 'researchers'" class="alice-an__panel">
          <div v-if="!data.skills.researchers.length" class="alice-an__empty">
            No answers in this window.
          </div>
          <table v-else class="alice-an__table">
            <thead>
              <tr>
                <th class="alice-an__th alice-an__th--name">Researcher</th>
                <th class="alice-an__th alice-an__th--num">Answers</th>
                <th class="alice-an__th alice-an__th--num">With a skill</th>
                <th class="alice-an__th">Share</th>
                <th class="alice-an__th alice-an__th--num">Skills</th>
                <th class="alice-an__th">Top skill</th>
                <th class="alice-an__th">Last skill use</th>
                <th class="alice-an__th alice-an__th--chev"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="r in data.skills.researchers" :key="r.uid">
                <tr
                  class="alice-an__tr alice-an__tr--clickable"
                  :class="{
                    'alice-an__tr--open': expandedSkillUser === r.uid,
                    'alice-an__tr--idle': !r.withSkill,
                  }"
                  @click="toggleSkillUser(r.uid)"
                >
                  <td class="alice-an__td alice-an__td--name">
                    <div class="alice-an__person">
                      <div
                        class="alice-an__avatar"
                        :style="
                          r.photo ? { backgroundImage: `url(${r.photo})` } : {}
                        "
                      >
                        <span v-if="!r.photo">{{ initials(r) }}</span>
                      </div>
                      <div class="alice-an__person-text">
                        <div class="alice-an__person-name">
                          {{ r.name || r.email || r.uid }}
                        </div>
                        <div v-if="r.email" class="alice-an__person-sub">
                          {{ r.email }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.answers.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.withSkill.toLocaleString("en-GB") }}
                  </td>
                  <td class="alice-an__td">
                    <span class="alice-an__share">
                      <span class="alice-an__bar-track">
                        <span
                          class="alice-an__bar-fill"
                          :style="{
                            width: `${percent(r.withSkill, r.answers)}%`,
                          }"
                        />
                      </span>
                      <span class="alice-an__share-value">{{
                        shareLabel(r.withSkill, r.answers)
                      }}</span>
                    </span>
                  </td>
                  <td class="alice-an__td alice-an__td--num">
                    {{ r.skills.length }}
                  </td>
                  <td class="alice-an__td alice-an__td--ellipsis">
                    <span v-if="r.skills.length">{{ r.skills[0].title }}</span>
                    <span v-else class="alice-an__muted">—</span>
                  </td>
                  <td class="alice-an__td alice-an__td--mono">
                    {{ formatRelative(r.lastSkillAt) }}
                  </td>
                  <td class="alice-an__td alice-an__td--chev">
                    <Icon
                      :name="
                        expandedSkillUser === r.uid
                          ? 'chevron-down'
                          : 'chevron-right'
                      "
                      :size="14"
                    />
                  </td>
                </tr>
                <tr v-if="expandedSkillUser === r.uid" class="alice-an__expand">
                  <td :colspan="8" class="alice-an__expand-cell">
                    <div class="alice-an__expand-block">
                      <div class="alice-an__expand-label">
                        Skills used ({{ r.skills.length }})
                      </div>
                      <ul v-if="r.skills.length" class="alice-an__bars">
                        <li
                          v-for="k in r.skills"
                          :key="k.id"
                          class="alice-an__bar-row"
                        >
                          <span class="alice-an__bar-label">{{ k.title }}</span>
                          <span class="alice-an__bar-track">
                            <span
                              class="alice-an__bar-fill"
                              :style="{
                                width: `${percent(k.uses, r.skills[0].uses)}%`,
                              }"
                            />
                          </span>
                          <span class="alice-an__bar-value">{{ k.uses }}</span>
                        </li>
                      </ul>
                      <div v-else class="alice-an__muted">
                        No skills used in this window.
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";

// Display names for known model ids — mirrors MODEL_OPTIONS in ProjectChat.vue.
const MODEL_TITLES = {
  "claude-opus-5-5": "Claude Opus 5.5",
  "claude-opus-5": "Claude Opus 5",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-sonnet-5-5": "Claude Sonnet 5.5",
  "claude-sonnet-5": "Claude Sonnet 5",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
  "claude-haiku-4-5": "Claude Haiku 4.5",
  "gpt-6-sol": "GPT-6 Sol",
  "gpt-6-luna": "GPT-6 Luna",
  "gpt-5.6-sol": "GPT-5.6 Sol",
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro",
  "gemini-3.8-flash": "Gemini 3.8 Flash",
  // Background jobs and voice messages, not in the chat picker.
  "gemini-3-flash-preview": "Gemini 3 Flash",
  "gemini-embedding-001": "Gemini Embedding",
  "gpt-5-mini": "GPT-5 mini (file extraction)",
  "gpt-4.1-mini": "GPT-4.1 mini (email digest)",
  "gpt-4o-mini-transcribe": "GPT-4o mini Transcribe",
  "gpt-image-2.5-sunburst": "GPT Image 2.5 Sunburst (MCP illustrations)",
};

// Independent per-query energy methodology (Epoch AI). Anthropic publishes no
// per-query figure; this is the most reputable average calculation available.
const ENERGY_SOURCE_URL =
  "https://epoch.ai/gradient-updates/how-much-energy-does-chatgpt-use";

// Ordered categorical palette for the per-project stack, drawn from the brand
// tokens so it matches the rest of the admin surface. Projects take these in
// cost order; "Zonder project" always renders in STACK_REST_COLOR instead, so it
// reads as a leftover rather than as another project.
const STACK_COLORS = [
  "var(--vp-black)",
  "var(--vp-turquoise-100)",
  "var(--vp-geel-100)",
  "var(--vp-rood-100)",
  "var(--vp-turquoise-60)",
  "var(--vp-geel-60)",
];
const STACK_REST_COLOR = "var(--vp-grijs-60)";
const STACK_OTHER_COLOR = "var(--vp-grijs-40)";
// Projects charted individually per month; the rest collapse into one segment.
const STACK_TOP_N = 6;

const TABS = [
  { key: "activity", label: "Activity" },
  { key: "cost", label: "Tokens & cost" },
  { key: "skills", label: "Skills" },
];

export default {
  name: "AdminAnalytics",
  components: { Icon },
  data() {
    return {
      windowDays: 30,
      windows: [
        { key: "7", label: "7d", value: 7 },
        { key: "30", label: "30d", value: 30 },
        { key: "90", label: "90d", value: 90 },
        { key: "all", label: "All", value: 0 },
      ],
      tabs: TABS,
      // Linkable: /admin/analytics?tab=skills opens on that tab.
      tab: TABS.some((t) => t.key === this.$route.query.tab)
        ? this.$route.query.tab
        : "activity",
      activityView: "researchers",
      skillsView: "skills",
      loading: false,
      error: null,
      data: null,
      expandedUser: null,
      expandedProject: null,
      expandedMonth: null,
      expandedSkill: null,
      expandedSkillUser: null,
      energySourceUrl: ENERGY_SOURCE_URL,
    };
  },
  computed: {
    headlineTiles() {
      if (this.tab === "cost") return this.usageKpiTiles;
      if (this.tab === "skills") return this.skillKpiTiles;
      return this.kpiTiles;
    },
    windowSub() {
      return this.windowDays ? `last ${this.windowDays} days` : "all time";
    },
    kpiTiles() {
      if (!this.data) return [];
      const k = this.data.kpis;
      return [
        {
          label: "Total messages",
          value: k.totalMessages.toLocaleString("en-GB"),
          sub: this.windowSub,
          color: "var(--vp-black)",
        },
        {
          label: "Active researchers",
          value: k.activeResearchers,
          sub: "sent ≥ 1 message",
          color: "var(--vp-turquoise-100)",
        },
        {
          label: "Avg per researcher",
          value: k.avgPerResearcher,
          sub: "messages / researcher",
          color: "var(--vp-geel-100)",
        },
        {
          label: "Attachments",
          value: k.totalAttachments.toLocaleString("en-GB"),
          sub: "files uploaded",
          color: "var(--vp-rood-100)",
        },
      ];
    },
    trendDays() {
      return this.padDays(this.data?.dailyTrend || [], (date) => ({
        date,
        count: 0,
      }));
    },
    trendMax() {
      return this.trendDays.reduce((m, d) => Math.max(m, d.count), 0);
    },
    usageKpiTiles() {
      const t = this.data?.usage?.totals;
      if (!t || !t.requests) return [];
      return [
        {
          label: "Est. cost",
          value: this.formatEur(t.costEur),
          sub: this.windowSub,
          color: "var(--vp-black)",
        },
        {
          label: "Tokens (incl. cache)",
          value: this.formatCount(this.totalTokens(t)),
          sub: `${t.requests.toLocaleString("en-GB")} requests`,
          color: "var(--vp-turquoise-100)",
        },
        {
          label: "Energy",
          value: this.formatEnergy(t.energyWh),
          sub: "estimate",
          color: "var(--vp-geel-100)",
        },
        {
          label: "CO₂",
          value: this.formatCo2(t.co2g),
          sub: "estimate",
          color: "var(--vp-groen-100)",
        },
      ];
    },
    skillKpiTiles() {
      const t = this.data?.skills?.totals;
      if (!t) return [];
      return [
        {
          label: "Skill uses",
          value: t.uses.toLocaleString("en-GB"),
          sub: this.windowSub,
          color: "var(--vp-black)",
        },
        {
          label: "Answers with a skill",
          value: this.shareLabel(t.answersWithSkill, t.answers),
          sub: `${t.answersWithSkill.toLocaleString(
            "en-GB"
          )} of ${t.answers.toLocaleString("en-GB")} answers`,
          color: "var(--vp-turquoise-100)",
        },
        {
          label: "Researchers using skills",
          value: t.researchersWithSkill,
          sub: `of ${t.researchers} who asked Alice`,
          color: "var(--vp-geel-100)",
        },
        {
          label: "Skills in use",
          value: t.skillsUsed,
          sub: `of ${t.skillsPublished} published`,
          color: "var(--vp-rood-100)",
        },
      ];
    },
    skillTrendDays() {
      return this.padDays(this.data?.skills?.dailyTrend || [], (date) => ({
        date,
        answers: 0,
        withSkill: 0,
      }));
    },
    skillTrendMax() {
      return this.skillTrendDays.reduce((m, d) => Math.max(m, d.answers), 0);
    },
    skillsSourceNote() {
      const s = this.data?.skills;
      if (!s) return "";
      if (this.skillsView === "researchers") {
        return "Answers in chat and team chat. MCP fetches don't say who made them, so they aren't in here.";
      }
      const since = (day) => (day ? ` since ${this.formatDate(day)}` : "");
      return `Chat and team chat count the answers a skill was active in${since(
        s.since
      )}; MCP fetches and Picked by Alice are counted${since(
        s.countersSince
      )}.`;
    },
    // Input+output plus the cached buckets — the true throughput. Used for the
    // admin volume figures; cost (costEur) already prices cache in separately.
    usageBreakdown() {
      const t = this.data?.usage?.totals;
      if (!t) return [];
      return [
        { label: "Input", value: this.formatTokens(t.inputTokens || 0) },
        { label: "Output", value: this.formatTokens(t.outputTokens || 0) },
        {
          label: "Cache read",
          value: this.formatTokens(t.cacheReadTokens || 0),
        },
        {
          label: "Cache write",
          value: this.formatTokens(t.cacheWriteTokens || 0),
        },
      ];
    },
    usageTrendDays() {
      return this.padDays(this.data?.usage?.dailyTrend || [], (date) => ({
        date,
        tokens: 0,
        costEur: 0,
      }));
    },
    usageTrendMax() {
      return this.usageTrendDays.reduce((m, d) => Math.max(m, d.costEur), 0);
    },
    // Server order is newest first — right for the table, but the chart reads
    // left-to-right in time, so that one gets its own reversed copy below.
    monthCost() {
      return (this.data?.usage?.byMonth || []).map((m) => {
        const projects = m.projects || [];
        const named = projects.filter((p) => !p.unattributed);
        return {
          ...m,
          projects,
          // "Zonder project" is a residual, not a project — don't count it.
          projectCount: named.length,
          topProject: named[0] || null,
          sources: m.sources || [],
        };
      });
    },
    // Colours are assigned once across the whole chart, by total cost over all
    // months — not per month. Otherwise a project would change colour from one
    // column to the next and the legend would mean nothing.
    monthColors() {
      const totals = new Map();
      this.monthCost.forEach((m) => {
        m.projects.forEach((p) => {
          if (p.unattributed) return;
          if (!totals.has(p.uid)) {
            totals.set(p.uid, { title: p.title, costEur: 0 });
          }
          totals.get(p.uid).costEur += p.costEur;
        });
      });
      const ranked = [...totals.entries()]
        .sort((a, b) => b[1].costEur - a[1].costEur)
        .slice(0, STACK_TOP_N);
      return new Map(
        ranked.map(([uid, v], i) => [
          uid,
          { title: v.title, color: STACK_COLORS[i % STACK_COLORS.length] },
        ])
      );
    },
    monthCostChart() {
      return [...this.monthCost].reverse().map((m) => ({
        ...m,
        segments: this.monthSegments(m),
      }));
    },
    monthCostMax() {
      return this.monthCost.reduce((max, m) => Math.max(max, m.costEur), 0);
    },
    monthLegend() {
      const items = [...this.monthColors.entries()].map(([uid, v]) => ({
        key: uid,
        title: v.title,
        color: v.color,
      }));
      // Only advertise the catch-all buckets if some month actually uses them.
      const used = new Set();
      this.monthCostChart.forEach((m) =>
        m.segments.forEach((s) => used.add(s.key))
      );
      if (used.has("__other__")) {
        items.push({
          key: "__other__",
          title: "Other projects",
          color: STACK_OTHER_COLOR,
        });
      }
      if (used.has("__none__")) {
        items.push({
          key: "__none__",
          title: "Zonder project",
          color: STACK_REST_COLOR,
        });
      }
      return items;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    // Total tokens including the cached buckets (input + output + cache
    // read + cache write) — the honest throughput figure for admin analytics.
    totalTokens(o) {
      if (!o) return 0;
      return (
        (o.inputTokens || 0) +
        (o.outputTokens || 0) +
        (o.cacheReadTokens || 0) +
        (o.cacheWriteTokens || 0)
      );
    },
    // Pad missing days with an empty entry so a bar chart is gap-free. "All"
    // has no fixed length, so that one shows the days the server returned.
    padDays(list, empty) {
      if (!this.windowDays) return list;
      const map = {};
      list.forEach((d) => (map[d.date] = d));
      const out = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      for (let i = this.windowDays - 1; i >= 0; i--) {
        const dt = new Date(today);
        dt.setDate(today.getDate() - i);
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        const key = `${dt.getFullYear()}-${m}-${day}`;
        out.push(map[key] || empty(key));
      }
      return out;
    },
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const fn = httpsCallable(functions, "getAliceAnalytics");
        const res = await fn({ windowDays: this.windowDays });
        if (res.data && res.data.error === 403) {
          this.error = "You don't have access to admin analytics.";
          this.data = null;
          return;
        }
        this.data = res.data;
      } catch (e) {
        console.error(e);
        this.error = `Failed to load analytics: ${e.message || e}`;
      } finally {
        this.loading = false;
      }
    },
    setWindow(value) {
      if (this.windowDays === value) return;
      this.windowDays = value;
      this.expandedUser = null;
      this.expandedProject = null;
      this.expandedSkill = null;
      this.expandedSkillUser = null;
      this.fetch();
    },
    setTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.$router.replace({ query: { ...this.$route.query, tab: key } });
    },
    toggleUser(uid) {
      this.expandedUser = this.expandedUser === uid ? null : uid;
    },
    toggleProject(uid) {
      this.expandedProject = this.expandedProject === uid ? null : uid;
    },
    toggleSkill(id) {
      this.expandedSkill = this.expandedSkill === id ? null : id;
    },
    toggleSkillUser(uid) {
      this.expandedSkillUser = this.expandedSkillUser === uid ? null : uid;
    },
    toggleMonth(month) {
      this.expandedMonth = this.expandedMonth === month ? null : month;
    },
    // Stack segments for one month, in the palette's order so colours stay put
    // between columns. Everything outside the top N collapses into one segment,
    // and the unattributed residual always sits on top.
    monthSegments(m) {
      if (!m.costEur) return [];
      const share = (cost) => (cost / m.costEur) * 100;
      const segments = [];
      let other = 0;
      let none = 0;

      m.projects.forEach((p) => {
        if (p.unattributed) {
          none += p.costEur;
        } else if (!this.monthColors.has(p.uid)) {
          other += p.costEur;
        }
      });

      this.monthColors.forEach((meta, uid) => {
        const hit = m.projects.find((p) => p.uid === uid);
        if (!hit || !hit.costEur) return;
        segments.push({
          key: uid,
          title: meta.title,
          color: meta.color,
          costEur: hit.costEur,
          share: share(hit.costEur),
        });
      });

      if (other > 0) {
        segments.push({
          key: "__other__",
          title: "Other projects",
          color: STACK_OTHER_COLOR,
          costEur: other,
          share: share(other),
        });
      }
      if (none > 0) {
        segments.push({
          key: "__none__",
          title: "Zonder project",
          color: STACK_REST_COLOR,
          unattributed: true,
          costEur: none,
          share: share(none),
        });
      }
      return segments;
    },
    monthBarHeight(costEur) {
      if (!this.monthCostMax) return 0;
      return Math.max(2, Math.round((costEur / this.monthCostMax) * 100));
    },
    monthProjectWidth(m, p) {
      // Against the widest row, not projects[0] — the unattributed residual is
      // appended last and can outweigh every named project.
      const max = (m.projects || []).reduce(
        (acc, row) => Math.max(acc, row.costEur),
        0
      );
      if (!max) return 0;
      return Math.max(2, Math.round((p.costEur / max) * 100));
    },
    monthShare(m, p) {
      if (!m.costEur) return "0%";
      return `${Math.round((p.costEur / m.costEur) * 100)}%`;
    },
    sourceSummary(sources) {
      return (sources || [])
        .map((s) => `${s.source} (${s.requests})`)
        .join(", ");
    },
    monthLabel(key) {
      const [y, mo] = String(key).split("-").map(Number);
      if (!y || !mo) return key;
      return new Date(y, mo - 1, 1).toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },
    monthShortLabel(key) {
      const [y, mo] = String(key).split("-").map(Number);
      if (!y || !mo) return key;
      return new Date(y, mo - 1, 1).toLocaleDateString("en-GB", {
        month: "short",
      });
    },
    trendBarHeight(count) {
      if (!this.trendMax) return 0;
      return Math.max(2, Math.round((count / this.trendMax) * 100));
    },
    skillBarHeight(answers) {
      if (!this.skillTrendMax) return 0;
      return Math.max(2, Math.round((answers / this.skillTrendMax) * 100));
    },
    percent(part, whole) {
      if (!whole) return 0;
      return Math.round((part / whole) * 100);
    },
    // For display: a share that rounds to 0 but isn't nothing reads "<1%".
    shareLabel(part, whole) {
      const pct = this.percent(part, whole);
      return part > 0 && pct === 0 ? "<1%" : `${pct}%`;
    },
    userBarHeight(count, user) {
      const max = user.dailyTrend.reduce((m, d) => Math.max(m, d.count), 0);
      if (!max) return 0;
      return Math.max(4, Math.round((count / max) * 100));
    },
    usageBarHeight(costEur) {
      if (!this.usageTrendMax) return 0;
      return Math.max(2, Math.round((costEur / this.usageTrendMax) * 100));
    },
    usageBarWidth(m) {
      const max = this.data?.usage?.byModel?.[0]?.costEur || 0;
      if (!max) return 0;
      return Math.max(2, Math.round((m.costEur / max) * 100));
    },
    usageUserBarWidth(u) {
      const max = this.data?.usage?.byUser?.[0]?.costEur || 0;
      if (!max) return 0;
      return Math.max(2, Math.round((u.costEur / max) * 100));
    },
    modelTitle(id) {
      return MODEL_TITLES[id] || id;
    },
    formatCount(n) {
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
      return `${Math.round(n)}`;
    },
    formatTokens(n) {
      return `${this.formatCount(n)} tokens`;
    },
    formatEnergy(wh) {
      if (wh >= 1000) return `${(wh / 1000).toFixed(1)} kWh`;
      return `${wh.toFixed(1)} Wh`;
    },
    formatCo2(g) {
      if (g >= 1000) return `${(g / 1000).toFixed(1)} kg`;
      return `${g.toFixed(1)} g`;
    },
    // Monthly figures run into the thousands, where a bare toFixed(2) reads
    // badly — same en-GB locale the token counts use.
    formatEur(n) {
      return `€${Number(n || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    initials(r) {
      const s = (r.name || r.email || r.uid || "?").trim();
      const parts = s.split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return s.slice(0, 2).toUpperCase();
    },
    formatDate(iso) {
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
    },
    formatRelative(ts) {
      if (!ts) return "—";
      const diff = Date.now() - ts;
      const m = 60000;
      const h = 3600000;
      const d = 86400000;
      if (diff < h) return `${Math.max(1, Math.round(diff / m))}m ago`;
      if (diff < d) return `${Math.round(diff / h)}h ago`;
      if (diff < 30 * d) return `${Math.round(diff / d)}d ago`;
      return new Date(ts).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    skillStatusLabel(status) {
      return (
        { draft: "offline", deleted: "deleted" }[status] || status || "offline"
      );
    },
  },
};
</script>

<style scoped lang="scss">
.alice-an {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-an__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-an__head-main {
  flex: 1;
  min-width: 0;
}
.alice-an__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-an__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-an__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-an__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 620px;
  line-height: 1.5;
}

/* Segmented control: the time window, and the breakdown inside a tab */
.alice-an__seg {
  display: inline-flex;
  border: 1px solid var(--border-hairline);
  height: 36px;
  flex-shrink: 0;
}
.alice-an__seg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: 0 14px;
  height: 100%;
  background: var(--vp-white);
  border: none;
  border-right: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-an__seg-btn:last-child {
  border-right: none;
}
.alice-an__seg-btn:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}
.alice-an__seg-btn--active,
.alice-an__seg-btn--active:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-an__seg-count {
  margin-left: 6px;
  font-weight: 600;
  letter-spacing: 0;
  opacity: 0.6;
}

/* States */
.alice-an__state {
  padding: 48px 32px;
  font-family: var(--font-ui);
  font-size: 14px;
  color: var(--fg-3);
}
.alice-an__state--error {
  color: var(--vp-rood-100);
}
.alice-an__empty {
  padding: 32px;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
  text-align: center;
}
.alice-an__muted {
  color: var(--fg-3);
}

/* ============== Body ============== */
.alice-an__body {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
}

/* ============== KPIs ============== */
.alice-an__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-an__kpi {
  position: relative;
  padding: 20px 24px;
}
.alice-an__kpi-divider {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 1px;
  background: var(--border-hairline);
}
.alice-an__kpi-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.alice-an__kpi-swatch {
  width: 8px;
  height: 8px;
  align-self: center;
  flex-shrink: 0;
}
.alice-an__kpi-value {
  font-family: var(--font-display);
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-an__kpi-label {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-an__kpi-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Section ============== */
.alice-an__section {
  padding: 24px 32px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-an__section-head {
  margin-bottom: 14px;
}
.alice-an__section-title {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--vp-black);
}
.alice-an__section-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  margin-top: 2px;
}

/* Trend sparkbars */
.alice-an__trend {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 96px;
  padding-top: 8px;
}
.alice-an__trend--mini {
  height: 56px;
  gap: 2px;
}
.alice-an__trend-bar {
  flex: 1;
  min-width: 4px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  background: var(--bg-muted);
  position: relative;
}
.alice-an__trend-fill {
  display: block;
  width: 100%;
  background: var(--vp-black);
  transition: height 0.2s ease;
}
.alice-an__trend-bar:hover .alice-an__trend-fill {
  background: var(--accent-100);
}
/* Two-part bar: height = all answers that day, dark part = the ones with a
   skill. The hover accent moves to the dark part. */
.alice-an__trend-fill--total,
.alice-an__trend-bar:hover .alice-an__trend-fill--total {
  display: flex;
  align-items: flex-end;
  background: var(--vp-grijs-40);
}
.alice-an__trend-part {
  display: block;
  width: 100%;
  background: var(--vp-black);
}
.alice-an__trend-bar:hover .alice-an__trend-part {
  background: var(--accent-100);
}

/* ============== Cost per month per project ============== */
.alice-an__stack {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 8px 0 0;
  overflow-x: auto;
}
.alice-an__stack-col {
  /* Capped so a single tracked month reads as a column, not a progress bar. */
  flex: 1 1 0;
  min-width: 44px;
  max-width: 92px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.alice-an__stack-bar {
  height: 132px;
  display: flex;
  align-items: flex-end;
  background: var(--bg-muted);
}
/* The track is the month's share of the tallest month; segments stack inside it
   from the bottom up, so column-reverse keeps the biggest project grounded. */
.alice-an__stack-track {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  transition: height 0.2s ease;
}
.alice-an__stack-seg {
  display: block;
  width: 100%;
  min-height: 1px;
}
.alice-an__stack-seg:hover {
  outline: 1px solid var(--vp-black);
  outline-offset: -1px;
}
.alice-an__stack-seg--rest {
  /* Hatched so the residual never passes for a project at a glance. */
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 3px,
    rgb(255 255 255 / 45%) 3px,
    rgb(255 255 255 / 45%) 6px
  );
}
.alice-an__stack-total {
  padding-top: 6px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--fg-2);
  text-align: center;
  white-space: nowrap;
}
.alice-an__stack-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  text-align: center;
}
.alice-an__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}
.alice-an__legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-2);
}
.alice-an__legend-swatch {
  width: 10px;
  height: 10px;
  flex: none;
  border: 1px solid var(--border-hairline);
}
.alice-an__table--months {
  margin-top: 20px;
}
.alice-an__stack-note {
  margin-top: 12px;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Token usage ============== */
.alice-an__usage-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin: 12px 0 20px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-an__usage-breakdown strong {
  color: var(--vp-black);
  font-weight: 700;
}
.alice-an__trend--usage {
  margin-bottom: 24px;
}
.alice-an__usage-grid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}
.alice-an__usage-grid .alice-an__bar-row {
  grid-template-columns: minmax(120px, 180px) 1fr minmax(120px, auto);
}
.alice-an__usage-model {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.alice-an__bar-value--wide {
  white-space: nowrap;
  font-size: 12px;
  color: var(--fg-2);
}
.alice-an__usage-source {
  color: var(--vp-blauw-120);
  text-decoration: underline;
  text-underline-offset: 2px;
}
/* Eco badge colors mirror alice-chat__eco-badge in ProjectChat.vue */
.alice-an__eco-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.alice-an__eco-badge--low {
  color: var(--vp-groen-100);
}
.alice-an__eco-badge--medium {
  color: var(--vp-geel-120);
}

/* ============== Tabs ============== */
.alice-an__tabs {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 32px;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-an__tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-3);
  transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.alice-an__tab:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-an__tab--active,
.alice-an__tab--active:hover {
  color: var(--vp-black);
  border-bottom-color: var(--accent-100);
  font-weight: 700;
  background: transparent;
}
.alice-an__tab-count {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
}
.alice-an__tab--active .alice-an__tab-count {
  color: var(--vp-black);
}

/* ============== Panel / table ============== */
.alice-an__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 32px 16px;
}
.alice-an__panel {
  padding: 0;
  flex: 1;
  min-height: 0;
}
.alice-an__table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-body);
  font-size: 14px;
}
.alice-an__th {
  text-align: left;
  padding: 14px 16px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
  white-space: nowrap;
}
.alice-an__th:first-child {
  padding-left: 32px;
}
.alice-an__th:last-child {
  padding-right: 32px;
}
.alice-an__th--num {
  text-align: right;
}
.alice-an__th--chev {
  width: 32px;
}
.alice-an__tr {
  border-bottom: 1px solid var(--border-hairline);
}
.alice-an__tr--clickable {
  cursor: pointer;
  transition: background 0.12s ease;
}
.alice-an__tr--clickable:hover,
.alice-an__tr--open {
  background: var(--bg-muted);
}
/* Nothing in this window: a skill nobody used, a researcher without skills. */
.alice-an__tr--idle .alice-an__td {
  color: var(--fg-3);
}
.alice-an__td {
  padding: 14px 16px;
  vertical-align: middle;
  color: var(--vp-black);
}
.alice-an__td:first-child {
  padding-left: 32px;
}
.alice-an__td:last-child {
  padding-right: 32px;
}
.alice-an__td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-family: var(--font-ui);
  font-weight: 600;
}
.alice-an__td--mono {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
  white-space: nowrap;
}
.alice-an__td--ellipsis {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-an__td--chev {
  width: 32px;
  color: var(--fg-3);
  text-align: right;
}

/* Person cell */
.alice-an__person {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.alice-an__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--vp-grijs-60);
  color: var(--vp-white);
  background-size: cover;
  background-position: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.alice-an__person-text {
  min-width: 0;
}
.alice-an__person-name {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-an__person-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alice-an__pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
}

/* Expanded row */
.alice-an__expand {
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-an__expand-cell {
  padding: 20px 32px 24px;
}
.alice-an__expand-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 32px;
}
.alice-an__expand-grid--even {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}
.alice-an__expand-block {
  min-width: 0;
}
.alice-an__expand-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 12px;
}

.alice-an__bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.alice-an__bar-row {
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr 56px;
  align-items: center;
  gap: 12px;
}
.alice-an__bar-label {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-an__bar-track {
  position: relative;
  display: block;
  height: 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
}
.alice-an__bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--vp-black);
  transition: width 0.2s ease;
}
/* Must follow .alice-an__bar-fill: same specificity, so source order decides.
   Greys out the unattributed row in the per-month breakdown. */
.alice-an__bar-fill--rest {
  background: var(--vp-grijs-60);
}
/* Share of a researcher's answers that used a skill */
.alice-an__share {
  display: grid;
  grid-template-columns: minmax(64px, 120px) 40px;
  align-items: center;
  gap: 10px;
}
.alice-an__share-value {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.alice-an__bar-value {
  text-align: right;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--vp-black);
}

@media (max-width: 1279px) {
  .alice-an__head {
    padding: 24px 20px 20px;
    flex-direction: column;
    align-items: flex-start;
  }
  .alice-an__kpis {
    grid-template-columns: 1fr 1fr;
  }
  .alice-an__kpi-divider {
    display: none;
  }
  .alice-an__kpi {
    border-bottom: 1px solid var(--border-hairline);
    border-right: 1px solid var(--border-hairline);
  }
  .alice-an__kpi:nth-child(2n) {
    border-right: none;
  }
  .alice-an__section {
    padding: 20px;
  }
  .alice-an__tabs {
    padding: 0 20px;
  }
  .alice-an__panel-head {
    padding: 16px 20px 12px;
  }
  .alice-an__th:first-child,
  .alice-an__td:first-child {
    padding-left: 20px;
  }
  .alice-an__th:last-child,
  .alice-an__td:last-child {
    padding-right: 20px;
  }
  .alice-an__expand-cell {
    padding: 16px 20px 20px;
  }
  .alice-an__expand-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}
</style>
